import type { ApiError } from "../api/client";

export interface FriendlyError {
  title: string;
  message: string;
  hint?: string;
}

const FIELD_HINTS: Record<string, { name: string; hint: string }> = {
  billed_total: { name: "invoice total", hint: "Choose the spreadsheet column containing the carrier's billed amount." },
  billed_weight: { name: "billed weight", hint: "Choose the column with the weight the carrier billed, in pounds." },
  invoice_number: { name: "invoice number", hint: "Choose the column with the carrier's invoice or freight bill number." },
  pro_number: { name: "PRO number", hint: "Choose the column with the carrier PRO number used to match shipments." },
  quoted_total: { name: "quoted total", hint: "Choose the column with the expected charge from the quote." },
  quoted_weight: { name: "quoted weight", hint: "Choose the column with the weight on the quote or BOL." },
};

/** Turn an API error into operator language: what happened and what to do. Never a traceback. */
export function friendlyError(error: ApiError): FriendlyError {
  switch (error.code) {
    case "MISSING_REQUIRED_COLUMN": {
      const field = error.field ? FIELD_HINTS[error.field] : undefined;
      return {
        title: field ? `We couldn't find the ${field.name}.` : "A required column isn't mapped.",
        message: error.message,
        hint: field?.hint ?? "Choose a spreadsheet column for every required field.",
      };
    }
    case "INVALID_COLUMN_MAPPING":
      return { title: "That column mapping doesn't work.", message: error.message };
    case "UNSUPPORTED_FILE_TYPE":
      return { title: "That file type isn't supported.", message: error.message, hint: "Upload an .xlsx or .csv file." };
    case "FILE_TOO_LARGE":
    case "REQUEST_TOO_LARGE":
      return { title: "That file is too large.", message: error.message, hint: "Split it into smaller files." };
    case "TOO_MANY_ROWS":
    case "TOO_MANY_COLUMNS":
    case "TOO_MANY_WORKSHEETS":
      return { title: "That file is over the size limits.", message: error.message };
    case "WORKBOOK_UNREADABLE":
      return {
        title: "We couldn't read that workbook.",
        message: error.message,
        hint: "Open it in Excel and save a fresh copy as .xlsx or .csv.",
      };
    case "EMPTY_SPREADSHEET":
      return { title: "There's nothing to audit in that file.", message: error.message };
    case "IMPORT_NOT_FOUND":
      return { title: "That upload has expired.", message: error.message, hint: "Start over and upload the files again." };
    case "IMPORT_VALIDATION_FAILED":
      return { title: "Some rows need fixing first.", message: error.message };
    case "AUDIT_NOT_FOUND":
      return { title: "Audit not found.", message: error.message };
    case "AUDIT_TIMEOUT":
      return { title: "That audit took too long.", message: error.message };
    case "NETWORK_ERROR":
      return { title: "Connection problem.", message: error.message };
    default:
      return { title: "Something went wrong.", message: error.message };
  }
}
